import { USER_ROLES } from "@via/utils/constants.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";
import { credentialOptions } from "@via/utils/form-validators.tsx";
import { GeneratePendingSshKeyResponse } from "common";
import { JSX, useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function SSHCredentialsForm({
  mode = "create",
  hasPrivateKey,
  managedByAdmin = false,
  data,
  role,
  register,
  errors,
  onClick,
  setValue,
}: {
  mode?: "create" | "edit";
  hasPrivateKey?: boolean;
  /** Hidden key viewed by a non administrator: the server does not say whether a private key is stored. */
  managedByAdmin?: boolean;
  data?: GeneratePendingSshKeyResponse | null;
  role?: any;
  register: any;
  errors: any;
  onClick?: any;
  setValue: any;
}): JSX.Element {
  // Edit starts locked: the stored key is never shown and is kept unless the user chooses Replace.
  const [replacing, setReplacing] = useState(mode === "create");

  useEffect(() => {
    if (data) {
      // sec-4-14: the private key stays on the server. The form carries only its single use id.
      setValue("ssh.publicKey", data.publicKey.trim(), {
        shouldValidate: true,
      });
      setValue("ssh.pendingKeyId", data.pendingKeyId);
    }
  }, [data]);

  return (
    <>
      <div className="space-y-4 pb-5">
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Credential Name <span className="text-red-400">*</span>
          </label>
          <div className="mt-2">
            <input
              data-testid="sshNameInput"
              {...register("ssh.name", credentialOptions.sshCredentials.name)}
              type="text"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Enter Your Credential Name"
            />
            {errors.ssh?.name && (
              <span className="text-xs text-rose-500">
                {errors.ssh.name.message}
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">
            <span className="flex flex-row items-center justify-between">
              <span>Public Key</span>
              {replacing && (
                <span
                  onClick={onClick}
                  className="cursor-pointer text-xs text-indigo-600 duration-200 ease-in-out hover:text-indigo-500">
                  Generate Keys for Me
                </span>
              )}
            </span>
          </label>
          <div className="mb-5 mt-2">
            <textarea
              rows={4}
              data-testid="sshPublicKeyInput"
              {...register("ssh.publicKey")}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Enter Your Public Key"
            />
          </div>
          <input type="hidden" {...register("ssh.pendingKeyId")} />
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Private Key{" "}
            {replacing && !data && <span className="text-red-400">*</span>}
          </label>
          <div className="mt-2">
            {!replacing && (
              <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span>
                  {managedByAdmin
                    ? "This key is managed by your administrator."
                    : hasPrivateKey
                      ? "A private key is stored"
                      : "No private key is stored"}
                </span>
                <button
                  type="button"
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-500"
                  onClick={() => setReplacing(true)}>
                  Replace
                </button>
              </div>
            )}
            {replacing && data && (
              <p className="text-sm text-gray-700">
                A new key pair was generated. Its private key is kept on the
                server and is saved when you click Save.
              </p>
            )}
            {replacing && !data && (
              <textarea
                rows={4}
                data-testid="sshPrivateKeyInput"
                {...register(
                  "ssh.privateKey",
                  credentialOptions.sshCredentials.privateKey
                )}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Paste Your Private Key"
              />
            )}
            {errors.ssh?.privateKey && (
              <span className="text-xs text-rose-500">
                {errors.ssh.privateKey.message}
              </span>
            )}
          </div>
          {role === USER_ROLES.ADMIN && (
            <label className="mt-3 flex items-center text-sm font-medium leading-6 text-gray-900">
              <input
                data-testid="sshHideUserSelector"
                {...register("ssh.hide")}
                type="checkbox"
                className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
              Hide SSH Keys from User
            </label>
          )}
        </div>
        <div className="flex">
          <span className="text-xs leading-5 text-gray-500">
            <span className="font-bold">Important Information:</span> Private
            Keys will be used for submitting jobs to the host. Therefore, the
            Public Key of the Private Key must be added to&nbsp;
            <span className="rounded-sm bg-gray-200 p-0.5 text-gray-800">
              ~/.ssh/authorized_keys
            </span>{" "}
            in the host by user.
            <br />
            <div className="mt-1">
              Please check the{" "}
              <Link
                to={getDocsUrl("/user-guides/profile-guide/ssh-credentials/")}
                className="font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
                target="blank">
                &quot;Adding Keys&quot;
              </Link>{" "}
              section for more information.
            </div>
          </span>
        </div>
      </div>
    </>
  );
}
